import React, { useEffect, useRef, useMemo } from 'react'
import { ConsentState } from '../../client/utils/onetrust-utils'
import { useGoogleAdsConversionTags } from './ads'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import TagManager from './TagManager'

interface GoogleTrackingProps {
  consentState: ConsentState
}

/**
 * GoogleTracking component manages both Google tracking services:
 *
 * 1. Google Tag Manager (GTM) - Classic container-based tag management system
 *    - Loaded via gtm.js from TagManager module
 *    - Allows managing tags through Google's web interface
 *    - Typically used for complex marketing setups
 *
 * 2. Google Global Site Tag (gtag.js) - Modern direct tracking approach
 *    - Loaded directly for Google Analytics and Google Ads
 *    - Simpler, code-based configuration
 *    - Used for conversion tracking and analytics
 *
 * Both services are loaded because different parts of the business may rely on
 * either approach for their tracking needs.
 */
export const GoogleTracking: React.FC<GoogleTrackingProps> = ({
  consentState
}) => {
  const { data: googleData } = useGoogleAdsConversionTags()
  const { applicableConfigs } = useRestaurant()
  const { pixelsConfig } = applicableConfigs

  // Google Tag Manager state
  const tagManagerInitialized = useRef(false)

  // Google Global Site Tag IDs (gtag.js)
  const gtagIds = useMemo(() => {
    const ids: string[] = []
    ids.push('AW-16843855258') // COO Discover page
    if (googleData?.conversionTagId) {
      ids.push(googleData.conversionTagId) // Restaurant-specific Google Ads
    }
    if (pixelsConfig?.googleAnalytics) {
      ids.push(pixelsConfig.googleAnalytics) // Restaurant-specific Analytics
    }
    return ids
  }, [googleData?.conversionTagId, pixelsConfig?.googleAnalytics])

  // Initialize Google Tag Manager (GTM)
  useEffect(() => {
    if (
      consentState.hasTargetingConsent &&
      pixelsConfig?.googleTagManager &&
      !tagManagerInitialized.current
    ) {
      TagManager.initialize({
        gtmId: pixelsConfig.googleTagManager
      })
      tagManagerInitialized.current = true
    }
  }, [pixelsConfig?.googleTagManager, consentState.hasTargetingConsent])

  // Initialize Google Global Site Tag (gtag.js)
  useEffect(() => {
    if (!consentState.hasTargetingConsent || gtagIds.length === 0) return

    // Initialize dataLayer and gtag function (shared with GTM)
    window.dataLayer = window.dataLayer || []
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer!.push(arguments)
      }
    }

    // Load gtag.js script
    const primaryTagId = gtagIds[0]
    const scriptId = `google-gtag-${primaryTagId}`

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script')
      script.id = scriptId
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryTagId}`
      document.head.appendChild(script)
    }

    // Initialize gtag
    window.gtag!('js', new Date())

    // Configure tracking IDs
    gtagIds.forEach((tagId) => {
      window.gtag!('config', tagId)
    })
  }, [consentState.hasTargetingConsent, gtagIds])

  // Handle consent mode updates for both GTM and gtag.js
  useEffect(() => {
    if (!window.gtag) return

    // Analytics storage (Performance consent)
    if (consentState.hasPerformanceConsent) {
      window.gtag('consent', 'update', {
        wait_for_update: 500,
        analytics_storage: 'granted'
      })
    } else {
      window.gtag('consent', 'update', {
        wait_for_update: 500,
        analytics_storage: 'denied'
      })
    }

    // Ad storage (Targeting consent)
    if (consentState.hasTargetingConsent) {
      window.gtag('consent', 'update', {
        wait_for_update: 500,
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      })
    } else {
      window.gtag('consent', 'update', {
        wait_for_update: 500,
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      })
    }
  }, [consentState.hasPerformanceConsent, consentState.hasTargetingConsent])

  return null
}
